import React, { useMemo, useState, useCallback, useContext, useEffect } from 'react'
import Table from 'react-bootstrap/Table'
import Button from 'react-bootstrap/Button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSort, faSortUp, faSortDown, faChevronRight, faChevronDown } from '@fortawesome/free-solid-svg-icons'
import { titleize } from 'helpers/utils'
// Aliased: a sibling component in this file takes a `getColumnDisplayName` prop.
import {
  getColumnDisplayName as resolveColumnDisplayName,
  hasSegmentDisplayChoice
} from 'dashboard/utils'
import { formatValueByColumn, formatDateTimeValue, looksLikeIsoDate } from '../helpers/chart-formatters'
import { apiGet } from 'api'
import AuthContext from 'context/auth-context'
import {
  GroupByOption,
  DateGroupingOption,
  DecimalPlacesOption,
  SegmentDisplayOption,
  SEGMENT_DISPLAY_DEFAULT,
  DateFormatOption,
  DATE_FORMAT_DEFAULT,
  WHOLE_NUMBER_AGGREGATIONS
} from './chart-options'
import SearchableColumnSelect from './searchable-column-select'
import { isNull, isUndefined } from 'lodash'
import InlineFetchError from 'shared/inline-fetch-error'

// Aggregation methods for column key parsing
const AGGREGATION_METHODS = ['distinct_count', 'count', 'sum', 'avg', 'average', 'min', 'max']

// Build a stable string key from a path array for use in React keys and expand-state sets.
const pathKey = (path) => (path || []).map((v) => (v === null || v === undefined ? '\u0000' : String(v))).join('\u0001')

// Convert a legacy flat { rowKey: { colKey: value } } payload into the new tree rows shape.
const legacyDataToRows = (data) =>
  Object.entries(data || {}).map(([rowKey, values]) => ({
    key: [rowKey],
    depth: 0,
    is_leaf: true,
    values: values || {},
    children: []
  }))

function PivotTable({ initialRows, rows: rowsProp, rowColumnMetas: rowColumnMetasProp, initialData, columnNames: initialColumnNames, rowColumnName, rowColumnDisplayAs, pivotRowDateGrouping, pivotRowDateFormat, hasMore: initialHasMore, totalCount: initialTotalCount, chartId, dataUrl, dashboardId, params, municipality, groupingName = null, sortBy: initialSortBy, sortDirection: initialSortDirection, expandAll = false, inlineGroups = false, totals: totalsProp, onRowClick = null }) {
  const token = useContext(AuthContext)

  const normalizedInitialRows = initialRows || rowsProp || legacyDataToRows(initialData)
  const rowColumnMetas = rowColumnMetasProp || (rowColumnName
    ? [{
        name: rowColumnName,
        display_as: rowColumnDisplayAs,
        date_grouping: pivotRowDateGrouping,
        date_format: pivotRowDateFormat
      }]
    : [{ name: 'Row', display_as: rowColumnDisplayAs }])

  const [allRows, setAllRows] = useState(normalizedInitialRows)
  const [columnNames, setColumnNames] = useState(initialColumnNames || {})
  // Grand totals over every group, not just the loaded page — the backend aggregates them
  // across the whole filtered set. Null when the chart has no Total row configured.
  const [totals, setTotals] = useState(totalsProp || null)
  const [hasMore, setHasMore] = useState(initialHasMore || false)
  const [totalCount, setTotalCount] = useState(initialTotalCount || 0)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [sortBy, setSortBy] = useState(initialSortBy || 'row')
  const [sortDirection, setSortDirection] = useState(initialSortDirection || 'desc')
  const [isSorting, setIsSorting] = useState(false)
  // Load-more / sort failures used to be console-only, leaving the table looking like
  // nothing happened. Hold a message so it can be shown under the table too; the
  // rejection itself stays in the console rather than in front of the user.
  const [fetchError, setFetchError] = useState(null)
  const [expanded, setExpanded] = useState(() => new Set())

  // When expandAll is true (e.g., PDF export context), always render every descendant expanded.
  const isNodeExpanded = useCallback(
    (path) => expandAll || expanded.has(pathKey(path)),
    [expanded, expandAll]
  )

  const toggleExpanded = useCallback((path) => {
    const key = pathKey(path)
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  // Reset state when inputs change
  useEffect(() => {
    setAllRows(normalizedInitialRows)
    setColumnNames(initialColumnNames || {})
    setTotals(totalsProp || null)
    setHasMore(initialHasMore || false)
    setTotalCount(initialTotalCount || 0)
    setSortBy(initialSortBy || 'row')
    setSortDirection(initialSortDirection || 'desc')
    setExpanded(new Set())
    setFetchError(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, initialRows, rowsProp, initialColumnNames, initialHasMore, initialTotalCount, initialSortBy, initialSortDirection, totalsProp])

  // Build pagination/sort params for API requests
  // Exclude column_aggregations since the backend fetches them from the database
  // and URLSearchParams can't properly serialize arrays of objects
  const buildPivotParams = useCallback((pivotOffset, sortColumn, sortDir) => {
    const { column_aggregations, ...restParams } = params || {}
    return {
      ...restParams,
      pivot_offset: pivotOffset,
      pivot_limit: 10,
      pivot_sort_by: sortColumn,
      pivot_sort_direction: sortDir
    }
  }, [params])

  const rowsFromResult = (result) => result.rows || legacyDataToRows(result.data)

  const handleLoadMore = useCallback(async () => {
    if (!chartId || isLoadingMore) return

    setIsLoadingMore(true)
    setFetchError(null)

    try {
      const result = await apiGet({
        url: dataUrl || `charts/${chartId}/data`,
        token,
        params: buildPivotParams(allRows.length, sortBy, sortDirection)
      })

      const newRows = rowsFromResult(result)
      setAllRows((prev) => [...prev, ...newRows])
      if (result.column_names) {
        setColumnNames((prev) => ({ ...prev, ...result.column_names }))
      }
      setHasMore(result.has_more || false)
    } catch (error) {
      console.error('Error loading more pivot table data:', error)
      setFetchError("Couldn't load more rows")
    } finally {
      setIsLoadingMore(false)
    }
  }, [token, chartId, dataUrl, allRows.length, buildPivotParams, isLoadingMore, sortBy, sortDirection])

  const handleSort = useCallback(async (columnKey) => {
    if (!chartId || isSorting) return

    const newDirection = sortBy === columnKey && sortDirection === 'desc' ? 'asc' : 'desc'

    setIsSorting(true)
    setFetchError(null)

    try {
      const result = await apiGet({
        url: dataUrl || `charts/${chartId}/data`,
        token,
        params: buildPivotParams(0, columnKey, newDirection)
      })

      setAllRows(rowsFromResult(result))
      if (result.column_names) setColumnNames(result.column_names)
      setTotals(result.totals || null)
      setHasMore(result.has_more || false)
      setTotalCount(result.total_count || 0)
      setSortBy(columnKey)
      setSortDirection(newDirection)
      setExpanded(new Set())
    } catch (error) {
      console.error('Error sorting pivot table data:', error)
      setFetchError("Couldn't sort this table")
    } finally {
      setIsSorting(false)
    }
  }, [chartId, dataUrl, token, buildPivotParams, sortBy, sortDirection, isSorting])


  // Header labels come from the shared resolver rather than a local whitelist of the
  // raw seg_one..seg_eight columns. That whitelist is why the pivot header read
  // "Segment One Group" while every other surface read "Fund": a rollup column's
  // database_column_name is seg_one_group, which the list never matched.
  const getColumnDisplayNameForSegment = useCallback(
    (columnInfo) =>
      columnInfo
        ? resolveColumnDisplayName(columnInfo, municipality, groupingName)
        : undefined,
    [municipality, groupingName]
  )

  // Parse column key into components: { workingKey, positionSuffix, aggregation }
  const parseColumnKey = useCallback((col) => {
    let workingCol = col
    let positionSuffix = ''

    const positionMatch = col.match(/_(\d+)$/)
    if (positionMatch) {
      positionSuffix = ` (#${positionMatch[1]})`
      workingCol = col.substring(0, col.lastIndexOf('_'))
    }

    const sortedAggregations = [...AGGREGATION_METHODS].sort((a, b) => b.length - a.length)
    let aggregation = null

    for (const method of sortedAggregations) {
      if (workingCol.toLowerCase().endsWith(`_${method}`)) {
        aggregation = method
        break
      }
    }

    return { workingCol, positionSuffix, aggregation }
  }, [])

  // Extract aggregation method from column key
  const getAggregationFromColumnKey = useCallback((columnKey) => {
    const { aggregation } = parseColumnKey(columnKey)
    return aggregation === 'average' ? 'avg' : aggregation
  }, [parseColumnKey])

  // Format aggregation method for display
  const formatAggregation = (method) =>
    method.toLowerCase() === 'distinct_count' ? 'DISTINCT COUNT' : method.toUpperCase()

  const { columns, visibleRows, leafRows, hasChildren } = useMemo(() => {
    if (!Array.isArray(allRows) || allRows.length === 0) {
      return { columns: [], visibleRows: [], leafRows: [], hasChildren: false }
    }

    // Discover aggregation columns from the first leaf/non-leaf with values
    const firstWithValues = (function findFirst(nodes) {
      for (const node of nodes) {
        if (node.values && Object.keys(node.values).length > 0) return node
        const fromChildren = findFirst(node.children || [])
        if (fromChildren) return fromChildren
      }
      return null
    })(allRows)

    const columnKeys = firstWithValues ? Object.keys(firstWithValues.values) : []
    const columnsList = columnKeys.map((col) => {
      const { workingCol, positionSuffix, aggregation } = parseColumnKey(col)
      const columnInfo = columnNames[col] || columnNames[workingCol]
      const originalColumnName = getColumnDisplayNameForSegment(columnInfo)

      let display
      if (aggregation && originalColumnName) {
        display = `${originalColumnName} (${formatAggregation(aggregation)})${positionSuffix}`
      } else if (aggregation) {
        const columnName = workingCol.substring(0, workingCol.length - `_${aggregation}`.length)
        display = `${titleize(columnName)} (${formatAggregation(aggregation)})${positionSuffix}`
      } else {
        display = originalColumnName ? `${originalColumnName}${positionSuffix}` : `${titleize(workingCol)}${positionSuffix}`
      }
      return { key: col, display }
    })

    // DFS to build a flat list of rows honoring current expand state.
    const flat = []
    let anyChildren = false
    const walk = (nodes) => {
      nodes.forEach((node) => {
        flat.push(node)
        if (node.children && node.children.length > 0) {
          anyChildren = true
          if (isNodeExpanded(node.key)) walk(node.children)
        }
      })
    }
    walk(allRows)

    // Flat list of only the deepest (leaf) rows, used for the inline-groups layout.
    const leaves = []
    const collectLeaves = (nodes) => {
      nodes.forEach((node) => {
        if (node.children && node.children.length > 0) collectLeaves(node.children)
        else leaves.push(node)
      })
    }
    collectLeaves(allRows)

    return { columns: columnsList, visibleRows: flat, leafRows: leaves, hasChildren: anyChildren }
  }, [allRows, columnNames, getColumnDisplayNameForSegment, parseColumnKey, isNodeExpanded])

  const formatRowSegment = useCallback((value, meta, dateGrouping) => {
    if (isUndefined(value) || isNull(value) || (typeof value === 'string' && value.trim() === '')) return 'None'
    const displayAs = meta?.display_as
    const dateFormat = meta?.date_format
    // Default the grouping to 'day' so a date segment always renders as a
    // date rather than falling through to numeric formatting (which yields '0').
    // The grouping decides the precision, the format how that date reads.
    if (displayAs === 'date') {
      return formatValueByColumn(
        value,
        { display_as: displayAs, date_format: dateFormat },
        dateGrouping || 'day'
      )
    }
    if (displayAs === 'money' || displayAs === 'two_digit_money') return formatValueByColumn(value, { display_as: displayAs }, null)
    // Catch untyped segments that still hold an ISO datetime string.
    if (typeof value === 'string') return looksLikeIsoDate(value) ? formatDateTimeValue(value, dateFormat) : value
    return formatValueByColumn(value, { display_as: displayAs }, null)
  }, [])

  // When inline-groups is enabled and there is more than one row grouping level,
  // render each grouping level as its own column (a flat/tabular layout) rather than
  // nesting child rows under an expand/collapse chevron.
  const useInline = inlineGroups && rowColumnMetas.length > 1
  const displayRows = useInline ? leafRows : visibleRows

  if (!displayRows.length) {
    return (
      <div className="text-center p-4">
        <p>No data available</p>
      </div>
    )
  }

  // A drillable row opens the transactions behind it: its key path, plus the labels it reads as.
  const drillRowProps = (row) => {
    if (!onRowClick) return {}
    const labels = row.key.map((value, level) => {
      const meta = rowColumnMetas[level]
      return formatRowSegment(value, meta, meta?.date_grouping || (level === 0 ? pivotRowDateGrouping : undefined))
    })
    return {
      onClick: () => onRowClick(row.key, labels),
      className: 'pivot-drill-row',
      title: 'Show transactions',
      'data-testid': 'pivot-drill-row'
    }
  }

  // Render the aggregation value cells for a given row, shared by both layouts.
  const renderValueCells = (row, { bold = false } = {}) =>
    columns.map((col) => {
      const value = row.values ? row.values[col.key] : undefined
      const columnMeta = columnNames[col.key]
      const aggregationMethod = getAggregationFromColumnKey(col.key)

      let displayValue
      if (isUndefined(value) || isNull(value)) {
        displayValue = '0'
      } else if (looksLikeIsoDate(value)) {
        // e.g. a min/max aggregation on a date column returns an ISO datetime.
        displayValue = formatDateTimeValue(value)
      } else {
        displayValue = formatValueByColumn(value, columnMeta, aggregationMethod)
      }

      return (
        <td
          key={`cell-${pathKey(row.key)}-${col.key}`}
          className="text-end"
          style={{ fontWeight: bold ? 600 : 400 }}
        >
          {displayValue}
        </td>
      )
    })

  // The Total row reuses the body's cell renderer so it picks up each measure's display
  // type and decimals choice. `key` only feeds React keys, so a sentinel is fine.
  const renderTotalsRow = () => {
    if (!totals || !Object.keys(totals).length || !columns.length) return null

    const labelSpan = useInline ? rowColumnMetas.length : 1
    return (
      <tfoot>
        <tr>
          <th
            className="position-sticky text-start"
            colSpan={labelSpan}
            style={{ left: 0, zIndex: 9, backgroundColor: '#f8f9fa', fontWeight: 700, paddingLeft: '8px' }}
          >
            Total
          </th>
          {renderValueCells({ key: ['\u0000total'], values: totals }, { bold: true })}
        </tr>
      </tfoot>
    )
  }

  const renderSortIndicator = (columnKey) => {
    if (sortBy !== columnKey) {
      return <FontAwesomeIcon icon={faSort} className="ms-1" style={{ color: '#ccc' }} />
    }
    return sortDirection === 'desc'
      ? <FontAwesomeIcon icon={faSortDown} className="ms-1" style={{ color: '#555' }} />
      : <FontAwesomeIcon icon={faSortUp} className="ms-1" style={{ color: '#555' }} />
  }

  return (
    <div className="overflow-auto position-relative">
      {isSorting && (
        <div
          className="position-absolute d-flex align-items-center justify-content-center"
          style={{ top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255, 255, 255, 0.7)', zIndex: 20 }}
        >
          <span className="text-muted">Sorting...</span>
        </div>
      )}

      {hasChildren && !expandAll && !useInline && (
        <div className="d-flex justify-content-end mb-2">
          <Button
            variant="light-primary"
            size="sm"
            onClick={() => {
              const allParentPaths = []
              const collect = (nodes) => nodes.forEach((n) => {
                if (n.children && n.children.length > 0) {
                  allParentPaths.push(pathKey(n.key))
                  collect(n.children)
                }
              })
              collect(allRows)
              const isAllExpanded = allParentPaths.length > 0 && allParentPaths.every((k) => expanded.has(k))
              setExpanded(isAllExpanded ? new Set() : new Set(allParentPaths))
            }}
          >
            {(() => {
              const allParentPaths = []
              const collect = (nodes) => nodes.forEach((n) => {
                if (n.children && n.children.length > 0) {
                  allParentPaths.push(pathKey(n.key))
                  collect(n.children)
                }
              })
              collect(allRows)
              const isAllExpanded = allParentPaths.length > 0 && allParentPaths.every((k) => expanded.has(k))
              return isAllExpanded ? 'Collapse all' : 'Expand all'
            })()}
          </Button>
        </div>
      )}

      <Table bordered size="sm" className="bg-white">
        <thead className="table-light">
          <tr>
            {useInline ? (
              rowColumnMetas.map((meta, level) => {
                const isFirst = level === 0
                return (
                  <th
                    key={`head-${level}`}
                    className={isFirst ? 'position-sticky' : ''}
                    style={{
                      ...(isFirst ? { left: 0, zIndex: 10 } : {}),
                      backgroundColor: '#f8f9fa',
                      cursor: isFirst ? 'pointer' : 'default',
                      minWidth: '160px'
                    }}
                    onClick={isFirst ? () => handleSort('row') : undefined}
                  >
                    {getColumnDisplayNameForSegment(meta)}
                    {isFirst && renderSortIndicator('row')}
                  </th>
                )
              })
            ) : (
              <th
                className="position-sticky"
                style={{ left: 0, zIndex: 10, backgroundColor: '#f8f9fa', cursor: 'pointer', minWidth: '200px' }}
                onClick={() => handleSort('row')}
              >
                {rowColumnMetas.map((m) => getColumnDisplayNameForSegment(m)).join(' / ')}
                {renderSortIndicator('row')}
              </th>
            )}
            {columns.map((col) => (
              <th
                key={col.key}
                className="text-center"
                style={{ minWidth: '120px', cursor: 'pointer' }}
                onClick={() => handleSort(col.key)}
              >
                {col.display}
                {renderSortIndicator(col.key)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {useInline
            ? leafRows.map((row, rowIndex) => {
                const rowKey = `row-${pathKey(row.key)}-${rowIndex}`

                return (
                  <tr key={rowKey} {...drillRowProps(row)}>
                    {rowColumnMetas.map((meta, level) => {
                      const isFirst = level === 0
                      const dateGrouping = meta?.date_grouping || (isFirst ? pivotRowDateGrouping : undefined)
                      // Repeat the group value in every leaf row so each cell is self-describing.
                      const formatted = formatRowSegment(row.key[level], meta, dateGrouping)

                      return (
                        <th
                          key={`seg-${level}`}
                          className={`text-start${isFirst ? ' position-sticky' : ''}`}
                          style={{
                            ...(isFirst ? { left: 0, zIndex: 9 } : {}),
                            backgroundColor: 'white',
                            paddingLeft: '8px',
                            fontWeight: 500
                          }}
                        >
                          {formatted}
                        </th>
                      )
                    })}
                    {renderValueCells(row)}
                  </tr>
                )
              })
            : visibleRows.map((row, rowIndex) => {
                const depth = row.depth || 0
                const segmentValue = row.key[depth]
                const meta = rowColumnMetas[depth]
                const dateGrouping = meta?.date_grouping || (depth === 0 ? pivotRowDateGrouping : undefined)
                const formatted = formatRowSegment(segmentValue, meta, dateGrouping)
                const hasKids = !row.is_leaf && row.children && row.children.length > 0
                const isOpen = isNodeExpanded(row.key)
                const rowKey = `row-${pathKey(row.key)}-${rowIndex}`
                const indentPx = depth * 16

                return (
                  <tr key={rowKey} {...drillRowProps(row)}>
                    <th
                      className="position-sticky text-start"
                      style={{
                        left: 0,
                        zIndex: 9,
                        backgroundColor: 'white',
                        paddingLeft: `${8 + indentPx}px`,
                        fontWeight: depth === 0 ? 600 : 500
                      }}
                    >
                      {hasKids ? (
                        <span
                          role="button"
                          aria-label={isOpen ? 'Collapse row' : 'Expand row'}
                          onClick={(e) => {
                            e.stopPropagation()
                            toggleExpanded(row.key)
                          }}
                          style={{ cursor: 'pointer', display: 'inline-block', width: '16px' }}
                        >
                          <FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronRight} style={{ color: '#666' }} />
                        </span>
                      ) : (
                        <span style={{ display: 'inline-block', width: '16px' }} />
                      )}
                      <span className="ms-1">{formatted}</span>
                    </th>
                    {renderValueCells(row, { bold: depth === 0 && hasKids })}
                  </tr>
                )
              })}
        </tbody>
        {renderTotalsRow()}
      </Table>

      {columns.length === 0 && (
        <div className="alert alert-info mt-3">
          <strong>No column aggregations found.</strong>
          <br />
          Please configure column aggregations for this pivot table chart to display data.
        </div>
      )}

      {columns.length > 0 && (
        <div className="mt-2 text-muted small">
          Showing {allRows.length} {useInline ? 'row' : 'top-level row'}{allRows.length !== 1 ? 's' : ''}{totalCount > 0 ? ` of ${totalCount}` : ''}
        </div>
      )}

      {fetchError && (
        <div className="mt-2 text-center">
          <InlineFetchError message={fetchError} />
        </div>
      )}

      {hasMore && (
        <div className="mt-3 text-center">
          <Button variant="outline-primary" size="sm" onClick={handleLoadMore} disabled={isLoadingMore}>
            {isLoadingMore ? 'Loading...' : 'Load More'}
          </Button>
        </div>
      )}
    </div>
  )
}

// Chart configuration for pivot tables
export const pivotTableConfig = {
  requiredFields: ['title', 'groupBy', 'columnAggregations'],

  validate: (formData) => {
    const errors = {}
    if (!formData.groupBy) {
      errors.groupBy = 'Group By (Rows) column is required for pivot tables'
    }
    // Validate column aggregations
    if (!formData.draftColumnAggregations || formData.draftColumnAggregations.length === 0) {
      errors.columnAggregations = 'At least one column aggregation is required'
    } else {
      formData.draftColumnAggregations.forEach((agg, index) => {
        if (!agg.pivot_column_id) {
          errors[`columnAgg${index}`] = 'Column selection is required'
        }
      })
    }
    return errors
  },

  isQueryEnabled: (formData, hasChartId) => {
    return !!(
      hasChartId &&
      formData.groupBy &&
      formData.savedColumnAggregations?.length > 0 &&
      formData.savedColumnAggregations.some((ca) => ca.pivot_column_id)
    )
  },

  buildChartParams: (formData) => {
    const additional = (formData.draftAdditionalGroupBys || [])
      .filter((g) => g && g.column_id)
    const additional_pivot_row_date_groupings = additional.reduce((acc, g) => {
      if (g.date_grouping) acc[g.column_id] = g.date_grouping
      return acc
    }, {})
    const additional_pivot_row_segment_displays = additional.reduce((acc, g) => {
      if (g.segment_display) acc[g.column_id] = g.segment_display
      return acc
    }, {})
    const additional_pivot_row_date_formats = additional.reduce((acc, g) => {
      if (g.date_format) acc[g.column_id] = g.date_format
      return acc
    }, {})
    return {
      pivot_row_column_id: formData.groupBy,
      pivot_row_date_grouping: formData.pivotRowDateGrouping,
      pivot_row_segment_display: formData.pivotRowSegmentDisplay || SEGMENT_DISPLAY_DEFAULT,
      pivot_row_date_format: formData.pivotRowDateFormat || DATE_FORMAT_DEFAULT,
      additional_pivot_row_column_ids: additional.map((g) => g.column_id),
      additional_pivot_row_date_groupings,
      additional_pivot_row_segment_displays,
      additional_pivot_row_date_formats,
      pivot_inline_groups: !!formData.pivotInlineGroups,
      pivot_show_totals: !!formData.pivotShowTotals,
      column_aggregations: formData.draftColumnAggregations
        .map((ca, index) => ({
          pivot_column_id: ca.pivot_column_id,
          aggregation_method: ca.aggregation_method,
          position: index,
          decimal_places: ca.decimal_places === 2 ? 2 : 0
        }))
        .filter((ca) => ca.pivot_column_id)
    }
  },

  // Build query params for API request
  buildQueryParams: (baseParams, formData, columns) => {
    const groupByCol = columns?.find((c) => c.id === formData.groupBy)
    const groupBy2Col = columns?.find((c) => c.id === formData.groupBy2)
    const params = { ...baseParams }
    params.pivot_row = groupByCol?.database_column_name
    if (formData.pivotRowDateGrouping && groupByCol?.display_as === 'date') {
      params.pivot_row_date_grouping = formData.pivotRowDateGrouping
    }
    // Part of the query key only: the backend reads the flag from the saved chart, but
    // including it makes toggling the Total row refetch instead of serving a stale response.
    params.pivot_show_totals = !!formData.savedPivotShowTotals
    // Same: the saved chart is the source of truth for the row's segment format, but
    // including it makes switching between code and description refetch.
    if (hasSegmentDisplayChoice(groupByCol)) {
      params.pivot_row_segment_display = formData.pivotRowSegmentDisplay || SEGMENT_DISPLAY_DEFAULT
    }
    // Same again for the row's date format: the saved chart decides how the label reads,
    // but including it here makes switching format refetch instead of serving a stale
    // response built with the old one.
    if (groupByCol?.display_as === 'date') {
      params.pivot_row_date_format = formData.pivotRowDateFormat || DATE_FORMAT_DEFAULT
    }
    if (formData.groupBy2) params.pivot_column = groupBy2Col?.database_column_name
    if (formData.valueColumn) {
      const valueCol = columns?.find((c) => c.id === formData.valueColumn)
      params.pivot_value = valueCol?.database_column_name
      params.pivot_agg = formData.aggregationMethod
    }
    // Use savedColumnAggregations to prevent queries during modal editing
    if (formData.savedColumnAggregations && formData.savedColumnAggregations.length > 0) {
      params.column_aggregations = formData.savedColumnAggregations
        .filter((ca) => ca.pivot_column_id)
        .map((ca, index) => ({
          pivot_column_id: ca.pivot_column_id,
          aggregation_method: ca.aggregation_method,
          position: index,
          // Part of the query key only: the backend reads the saved aggregations from the
          // database, but including this makes a decimals change refetch and re-render.
          decimal_places: ca.decimal_places === 2 ? 2 : 0
        }))
    }
    return params
  },

  // Render chart-specific options in the modal form
  renderOptions: ({
    Form,
    Button,
    formState,
    setters,
    validationErrors,
    hasSubmitted,
    getFilteredColumns,
    getValueColumns,
    getAggregationOptions,
    getColumnDisplayName,
    getAggregationLabel,
    municipality,
    columns
  }) => {
    const {
      groupBy,
      pivotRowDateGrouping,
      pivotRowSegmentDisplay,
      pivotRowDateFormat,
      draftColumnAggregations,
      draftAdditionalGroupBys = [],
      pivotInlineGroups = false,
      pivotShowTotals = false
    } = formState
    const {
      setGroupBy,
      setPivotRowDateGrouping,
      setPivotRowSegmentDisplay,
      setPivotRowDateFormat,
      setDraftColumnAggregations,
      setDraftAdditionalGroupBys,
      setPivotInlineGroups,
      setPivotShowTotals,
      setValidationErrors
    } = setters
    const filteredColumns = getFilteredColumns()

    // getValueColumns() offers numeric columns plus ThirdLine ID, because those are the ones you can
    // sum or average. But count/distinct_count are valid on ANY column (see getAggregationOptions),
    // and the backend counts whatever it is given — so a saved measure like "count of Check Number"
    // sits outside this list. react-select renders a value it has no option for as its placeholder,
    // which made a configured row read as "Select column..." and put it one keystroke away from
    // being silently re-pointed. Keep whatever is already selected in the options.
    const columnOptionsFor = (selectedId) => {
      const options = getValueColumns()
      if (!selectedId || options.some((c) => String(c.id) === String(selectedId))) return options

      const selected = columns?.find((c) => String(c.id) === String(selectedId))
      return selected ? [...options, selected] : options
    }

    // The measure's column when its cells can carry a format choice, undefined otherwise.
    // Every measure but a count renders through the number formatter, so the choice applies
    // to all of them. Counts are whole records, and a min/max over a date column renders as
    // a date rather than a number.
    const formattableColumnFor = (agg) => {
      if (!agg.pivot_column_id) return undefined
      if (WHOLE_NUMBER_AGGREGATIONS.has(agg.aggregation_method)) return undefined

      const selected = columns?.find((c) => String(c.id) === String(agg.pivot_column_id))
      return selected && selected.display_as !== 'date' ? selected : undefined
    }

    const updateAdditional = (next) => setDraftAdditionalGroupBys(next.map((g, i) => ({ ...g, position: i })))

    // Only the raw account segments carry a code alongside their description, so only they
    // get the description/code/both choice.
    const isSegmentGrouping = (columnId) =>
      hasSegmentDisplayChoice(columns?.find((c) => String(c.id) === String(columnId)))

    // Only a date grouping renders a date, so only it gets the choice of how that
    // date reads.
    const isDateGrouping = (columnId) =>
      columns?.find((c) => String(c.id) === String(columnId))?.display_as === 'date'

    return (
      <>
        <GroupByOption
          Form={Form}
          groupBy={groupBy}
          setGroupBy={setGroupBy}
          validationErrors={validationErrors}
          setValidationErrors={setValidationErrors}
          hasSubmitted={hasSubmitted}
          getGroupingColumns={() => filteredColumns}
          getColumnDisplayName={getColumnDisplayName}
          municipality={municipality}
          label="Group By (Rows)"
        />

        <DateGroupingOption
          Form={Form}
          xAxisColumn={groupBy}
          xAxisDateGrouping={pivotRowDateGrouping}
          setXAxisDateGrouping={setPivotRowDateGrouping}
          columns={columns}
        />

        {isSegmentGrouping(groupBy) && (
          <SegmentDisplayOption
            Form={Form}
            block
            inputId="pivot-row-segment-display"
            label="Show Rows As"
            segmentDisplay={pivotRowSegmentDisplay}
            setSegmentDisplay={setPivotRowSegmentDisplay}
          />
        )}

        {isDateGrouping(groupBy) && (
          <DateFormatOption
            Form={Form}
            block
            inputId="pivot-row-date-format"
            label="Date Format"
            dateFormat={pivotRowDateFormat}
            setDateFormat={setPivotRowDateFormat}
          />
        )}

        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <Form.Label className="mb-0">Additional Group By (optional)</Form.Label>
            {draftAdditionalGroupBys.length < 4 && (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() =>
                  updateAdditional([
                    ...draftAdditionalGroupBys,
                    {
                      column_id: '',
                      date_grouping: 'day',
                      segment_display: SEGMENT_DISPLAY_DEFAULT,
                      date_format: DATE_FORMAT_DEFAULT
                    }
                  ])
                }
              >
                + Add Group By
              </Button>
            )}
          </div>

          {draftAdditionalGroupBys.map((g, index) => {
            const selectedCol = filteredColumns?.find((c) => c.id === g.column_id)
            const isDate = selectedCol?.display_as === 'date'
            return (
              <div key={`additional-group-by-${index}`} className="border rounded p-2 mb-2 bg-light">
                <div className="d-flex gap-2 align-items-center">
                  <div className="flex-grow-1">
                    <Form.Label className="small mb-1">Group {index + 2}</Form.Label>
                    <SearchableColumnSelect
                      inputId={`addl-group-by-select-${index}`}
                      columns={filteredColumns}
                      value={g.column_id}
                      onChange={(val) => {
                        const next = [...draftAdditionalGroupBys]
                        next[index] = { ...next[index], column_id: val }
                        updateAdditional(next)
                      }}
                      getColumnDisplayName={getColumnDisplayName}
                      municipality={municipality}
                    />
                  </div>

                  {isSegmentGrouping(g.column_id) && (
                    <SegmentDisplayOption
                      Form={Form}
                      inputId={`addl-group-by-segment-display-${index}`}
                      segmentDisplay={g.segment_display}
                      setSegmentDisplay={(value) => {
                        const next = [...draftAdditionalGroupBys]
                        next[index] = { ...next[index], segment_display: value }
                        updateAdditional(next)
                      }}
                    />
                  )}

                  {isDate && (
                    <div style={{ width: '120px' }}>
                      <Form.Label className="small mb-1">Date Grouping</Form.Label>
                      <Form.Select
                        size="sm"
                        value={g.date_grouping || 'day'}
                        onChange={(e) => {
                          const next = [...draftAdditionalGroupBys]
                          next[index] = { ...next[index], date_grouping: e.target.value }
                          updateAdditional(next)
                        }}
                      >
                        <option value="day">Day</option>
                        <option value="week">Week</option>
                        <option value="month">Month</option>
                        <option value="year">Year</option>
                      </Form.Select>
                    </div>
                  )}

                  {isDate && (
                    <DateFormatOption
                      Form={Form}
                      inputId={`addl-group-by-date-format-${index}`}
                      dateFormat={g.date_format}
                      setDateFormat={(value) => {
                        const next = [...draftAdditionalGroupBys]
                        next[index] = { ...next[index], date_format: value }
                        updateAdditional(next)
                      }}
                      width="140px"
                    />
                  )}

                  <div className="d-flex flex-column gap-1" style={{ width: '60px' }}>
                    {index > 0 && (
                      <Button
                        variant="light-primary"
                        size="sm"
                        onClick={() => {
                          const next = [...draftAdditionalGroupBys]
                          const item = next.splice(index, 1)[0]
                          next.splice(index - 1, 0, item)
                          updateAdditional(next)
                        }}
                        style={{ fontSize: '0.7rem', padding: '0.1rem 0.3rem' }}
                      >
                        ↑
                      </Button>
                    )}
                    {index < draftAdditionalGroupBys.length - 1 && (
                      <Button
                        variant="light-primary"
                        size="sm"
                        onClick={() => {
                          const next = [...draftAdditionalGroupBys]
                          const item = next.splice(index, 1)[0]
                          next.splice(index + 1, 0, item)
                          updateAdditional(next)
                        }}
                        style={{ fontSize: '0.7rem', padding: '0.1rem 0.3rem' }}
                      >
                        ↓
                      </Button>
                    )}
                  </div>

                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => updateAdditional(draftAdditionalGroupBys.filter((_, i) => i !== index))}
                    style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem' }}
                  >
                    ×
                  </Button>
                </div>
              </div>
            )
          })}

          {draftAdditionalGroupBys.length > 0 && (
            <Form.Check
              type="switch"
              id="pivot-inline-groups-switch"
              className="mt-2"
              label="Display groups inline (separate columns instead of nested rows)"
              checked={!!pivotInlineGroups}
              onChange={(e) => setPivotInlineGroups(e.target.checked)}
            />
          )}
        </div>

        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <Form.Label className="mb-0">Column Aggregations</Form.Label>
            {draftColumnAggregations.length < 5 && (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => {
                  setDraftColumnAggregations([
                    ...draftColumnAggregations,
                    {
                      pivot_column_id: '',
                      aggregation_method: 'count',
                      position: draftColumnAggregations.length,
                      decimal_places: 0
                    }
                  ])
                }}
              >
                + Add Column
              </Button>
            )}
          </div>

          {draftColumnAggregations.map((agg, index) => (
            <div key={index} className="border rounded p-2 mb-2 bg-light">
              <div className="d-flex gap-2 align-items-center">
                <div className="flex-grow-1">
                  <Form.Label className="small mb-1">
                    Column {index + 1}
                  </Form.Label>
                  <SearchableColumnSelect
                    inputId={`column-agg-select-${index}`}
                    columns={columnOptionsFor(agg.pivot_column_id)}
                    value={agg.pivot_column_id}
                    onChange={(newColumnId) => {
                      const newAggregations = [...draftColumnAggregations]
                      newAggregations[index].pivot_column_id = newColumnId

                      const availableOptions = getAggregationOptions(newColumnId)
                      if (
                        !availableOptions.includes(
                          newAggregations[index].aggregation_method
                        )
                      ) {
                        newAggregations[index].aggregation_method =
                          availableOptions[0]
                      }

                      setDraftColumnAggregations(newAggregations)
                      if (validationErrors[`columnAgg${index}`]) {
                        setValidationErrors((prev) => ({
                          ...prev,
                          [`columnAgg${index}`]: undefined
                        }))
                      }
                    }}
                    getColumnDisplayName={getColumnDisplayName}
                    municipality={municipality}
                    isInvalid={
                      hasSubmitted && !!validationErrors[`columnAgg${index}`]
                    }
                  />
                  {hasSubmitted && validationErrors[`columnAgg${index}`] && (
                    <div className="invalid-feedback d-block">
                      {validationErrors[`columnAgg${index}`]}
                    </div>
                  )}
                </div>

                <div style={{ width: '120px' }}>
                  <Form.Label className="small mb-1">Method</Form.Label>
                  <Form.Select
                    size="sm"
                    value={agg.aggregation_method}
                    onChange={(e) => {
                      const newAggregations = [...draftColumnAggregations]
                      newAggregations[index].aggregation_method = e.target.value
                      setDraftColumnAggregations(newAggregations)
                    }}
                  >
                    {getAggregationOptions(agg.pivot_column_id).map(
                      (option) => (
                        <option key={`${index}-${option}`} value={option}>
                          {getAggregationLabel(option)}
                        </option>
                      )
                    )}
                  </Form.Select>
                </div>

                {formattableColumnFor(agg) && (
                  <DecimalPlacesOption
                    Form={Form}
                    inputId={`column-agg-decimals-${index}`}
                    displayAs={formattableColumnFor(agg).display_as}
                    decimalPlaces={agg.decimal_places}
                    setDecimalPlaces={(places) => {
                      const newAggregations = [...draftColumnAggregations]
                      newAggregations[index] = {
                        ...newAggregations[index],
                        decimal_places: places
                      }
                      setDraftColumnAggregations(newAggregations)
                    }}
                    width="130px"
                  />
                )}

                <div
                  className="d-flex flex-column gap-1"
                  style={{ width: '60px' }}
                >
                  {index > 0 && (
                    <Button
                      variant="light-primary"
                      size="sm"
                      onClick={() => {
                        const newAggregations = [...draftColumnAggregations]
                        const item = newAggregations.splice(index, 1)[0]
                        newAggregations.splice(index - 1, 0, item)
                        setDraftColumnAggregations(
                          newAggregations.map((agg, i) => ({
                            ...agg,
                            position: i
                          }))
                        )
                      }}
                      style={{
                        fontSize: '0.7rem',
                        padding: '0.1rem 0.3rem'
                      }}
                    >
                      ↑
                    </Button>
                  )}
                  {index < draftColumnAggregations.length - 1 && (
                    <Button
                      variant="light-primary"
                      size="sm"
                      onClick={() => {
                        const newAggregations = [...draftColumnAggregations]
                        const item = newAggregations.splice(index, 1)[0]
                        newAggregations.splice(index + 1, 0, item)
                        setDraftColumnAggregations(
                          newAggregations.map((agg, i) => ({
                            ...agg,
                            position: i
                          }))
                        )
                      }}
                      style={{
                        fontSize: '0.7rem',
                        padding: '0.1rem 0.3rem'
                      }}
                    >
                      ↓
                    </Button>
                  )}
                </div>

                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => {
                    const newAggregations = draftColumnAggregations.filter(
                      (_, i) => i !== index
                    )
                    setDraftColumnAggregations(
                      newAggregations.map((agg, i) => ({
                        ...agg,
                        position: i
                      }))
                    )
                  }}
                  style={{
                    fontSize: '0.7rem',
                    padding: '0.2rem 0.4rem'
                  }}
                >
                  ×
                </Button>
              </div>
            </div>
          ))}

          {draftColumnAggregations.length === 0 && (
            <div
              className={`text-center py-2 border border-dashed rounded ${
                hasSubmitted && validationErrors.columnAggregations
                  ? 'border-danger text-danger'
                  : 'text-muted'
              }`}
            >
              <div className="small">
                No column aggregations defined. Click "Add Column" to add your
                first aggregation.
              </div>
              {hasSubmitted && validationErrors.columnAggregations && (
                <div className="text-danger small mt-1">
                  {validationErrors.columnAggregations}
                </div>
              )}
            </div>
          )}

          <Form.Check
            type="switch"
            id="pivot-show-totals-switch"
            className="mt-2"
            label="Include a Total row"
            checked={!!pivotShowTotals}
            onChange={(e) => setPivotShowTotals(e.target.checked)}
          />
          <Form.Text className="text-muted">
            Totals cover every group, not just the rows currently loaded.
          </Form.Text>
        </div>
      </>
    )
  }
}

export default PivotTable
